import React, { forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Select, SelectChangeEvent } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { useDecision } from '@optimizely/react-sdk';
import colors from '../../../common/colors';
import { updatePizzaSummary } from '../../../builders/pizza/slice/pizza.slice';
import { mobileStartBreakpoint } from '../../../materialUi/theme';
import { Pizza } from '@/builders/pizza/dataTransformers/builderTypes';
import { RootState } from '@/rootStateTypes';
import { updateDeal } from '@/builders/deals/slice/deal.slice';
import { selectDeal } from '@/builders/deals/slice/selectors/dealsSelectors';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120
  },
  selectEmpty: {
    marginTop: theme.spacing(2)
  },
  quantitySelect: {
    borderRadius: '8px 0 0 8px',
    paddingLeft: '11px',
    borderRight: '0',
    paddingRight: '0 !important',
    '&:focus': {
      borderRadius: '8px 0 0 8px'
    },
    height: '100%',
    minWidth: '50px',
    [theme.breakpoints.up(mobileStartBreakpoint)]: {
      height: '25px'
    }
  },
  quantityBtn: {
    width: '58px',
    borderRadius: '8px 0 0 8px'
  },
  icon: {
    right: '8px',
    color: colors.gray900
  },
  screenReadersOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0'
  }
}));

const getPizza = (state: RootState): Pizza => state.domain.pizza;

interface RecipeQuantitySelectorProps {
  quantity?: number;
  isDeal?: boolean;
  onUpdateQuantity?: (quantity: number) => void;
  disabled?: boolean;
  maxQuantity?: number;
}

const ArrowDown = forwardRef<SVGSVGElement, React.ComponentProps<typeof KeyboardArrowDownOutlinedIcon>>((props, ref) => (
  <KeyboardArrowDownOutlinedIcon {...props} ref={ref} style={{ cursor: "pointer" }} />
));

/**
 * @deprecated Use `@noqo/clients/web/shared-components/molecules/QuantityPicker/QuantityPickerCTA` instead.
 */
const RecipeQuantitySelector = ({ quantity = 1, isDeal = false, onUpdateQuantity, disabled = false, maxQuantity = 10 }: RecipeQuantitySelectorProps): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const pizza = useSelector(getPizza);
  const deal = useSelector(selectDeal);
  const [{
    enabled: isSingleStepPizzaDealQuantityPickerEnabled, variationKey
  }] = useDecision('fr-dtg-218-quantity-picker-single-step-pizza-deal');

  const [{
    enabled: isMultiStepQtyPickerEnabled
  }] = useDecision('fr-ims-200-multistep-select-enable');

  const isSingleStepDealQtyPickerABTestOn = isSingleStepPizzaDealQuantityPickerEnabled && variationKey === 'control_qty_pick_pizza_deal_on';

  const quantitySelectOptions = React.useMemo(() => {
    const totalOptions = Math.max(1, maxQuantity);
    return Array.from({ length: totalOptions }, (_, i) => (
      <option key={i + 1} value={i + 1}>
        {i + 1}
      </option>
    ));
  }, [maxQuantity]);

  const handleQuantityChange = (event?: SelectChangeEvent<string | unknown>) => {
    let selectedQuantity = parseInt(event?.target.value as string ?? '', 10);

    if (Number.isNaN(selectedQuantity)) {
      selectedQuantity = 1;
      console.error('Unable to parse selected quantity');
    }

    if (onUpdateQuantity) {
      return onUpdateQuantity(selectedQuantity);
    }
    if (isSingleStepDealQtyPickerABTestOn && isDeal && deal) {
      return dispatch(updateDeal({
        ...deal.data,
        quantity: selectedQuantity
      }));
    }
    return dispatch(updatePizzaSummary({
      id: pizza.id,
      name: pizza.name,
      splittable: pizza.splittable,
      price: pizza.price,
      quantity: selectedQuantity
    }));
  };

  return (
    <>
      <Select
        native
        className={classes.quantityBtn}
        onChange={handleQuantityChange}
        IconComponent={ArrowDown}
        disabled={isMultiStepQtyPickerEnabled ? false : disabled}
        value={quantity.toString()}
        inputProps={{
          name: 'quantity',
          'aria-label': 'quantity',
          id: 'pizza-quantity-select',
          'data-testid': 'pizza-quantity-select',
          className: classes.quantitySelect,
          tabIndex: 0
        }}
      >
        {quantitySelectOptions}
      </Select>
    </>
  );
};

export default RecipeQuantitySelector;
